import { inject, Injectable, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Nullable } from 'primeng/ts-helpers';
import { AuthService } from '../../../../../../core/services/auth.service';
import { LanguageService } from '../../../../../../core/services/language.service';
import { UserConfigurationService } from '../../../../../../core/services/user-configuration.service';
import { FieldLimit } from '../../../../../../shared/enums/field-limit.enum';
import { Modules } from '../../../../../../shared/enums/modules.enum';
import { AccessService } from '../../../../../../shared/services/access.service';
import { CreateStockCommand } from '../../../contract/create-stock-command.contract';
import { UpdateStockCommand } from '../../../contract/update-stock-command.contract';
import { ViewStock } from '../../../contract/view-stock.contract';
import { isLocationType, StockType } from '../../../enums/stock-type.enum';

export interface StockForm {
  stockType: FormControl<Nullable<StockType>>;
  stockData: FormGroup<StockDataForm>;
  stockAddress: FormGroup<StockAddressForm>;
}

export interface StockDataForm {
  stockName: FormControl<Nullable<string>>;
  stockCostCenter: FormControl<Nullable<string>>;
  organisationId: FormControl<Nullable<string>>;
}

export interface StockAddressForm {
  street: FormControl<Nullable<string>>;
  streetNumber: FormControl<Nullable<string>>;
  postalCode: FormControl<Nullable<string>>;
  city: FormControl<Nullable<string>>;
  createGeofence: FormControl<Nullable<boolean>>;
  lat: FormControl<Nullable<number>>;
  lon: FormControl<Nullable<number>>;
}

@Injectable()
export class StockAddEditDialogFormController {
  private readonly languageService = inject(LanguageService);
  private readonly authService = inject(AuthService);
  private readonly accessService = inject(AccessService);
  private readonly userConfigurationService = inject(UserConfigurationService);

  private form!: FormGroup<StockForm>;
  private readonly $stock = signal<Nullable<ViewStock>>(null);

  readonly stockNameErrorMessages = {
    required: this.translate('shared.validation.requiredField'),
    maxlength: this.translate('shared.validation.maxCharacters', { value: FieldLimit.NAME }),
  };
  readonly stockCostCenterErrorMessages = {
    maxlength: this.translate('shared.validation.maxCharacters', { value: FieldLimit.COST_CENTER }),
  };
  readonly organisationErrorMessages = {
    required: this.translate('shared.validation.requiredField'),
  };
  readonly streetErrorMessages = {
    maxlength: this.translate('shared.validation.maxCharacters', { value: FieldLimit.STREET }),
  };
  readonly streetNumberErrorMessages = {
    maxlength: this.translate('shared.validation.maxCharacters', { value: FieldLimit.STREET_NUMBER }),
  };
  readonly postalCodeErrorMessages = {
    maxlength: this.translate('shared.validation.maxCharacters', { value: FieldLimit.POSTAL_CODE }),
  };
  readonly cityErrorMessages = {
    maxlength: this.translate('shared.validation.maxCharacters', { value: FieldLimit.CITY }),
  };

  buildForm(stock?: ViewStock): FormGroup<StockForm> {
    this.$stock.set(stock);
    this.form = new FormGroup<StockForm>({
      stockType: new FormControl(stock?.stockType ?? null, Validators.required),
      stockData: new FormGroup<StockDataForm>({
        stockName: new FormControl(stock?.stockName || null, [
          Validators.required,
          Validators.maxLength(FieldLimit.NAME),
        ]),
        stockCostCenter: new FormControl(stock?.stockCostCenter || null, [
          Validators.maxLength(FieldLimit.COST_CENTER),
        ]),
        organisationId: new FormControl(
          { value: stock?.organisationId || null, disabled: stock?.stockType === StockType.MAIN },
          stock?.stockType === StockType.MAIN ? null : Validators.required,
        ),
      }),
      stockAddress: new FormGroup<StockAddressForm>({
        street: new FormControl(stock?.stockAddress?.street || null, [Validators.maxLength(FieldLimit.STREET)]),
        streetNumber: new FormControl(stock?.stockAddress?.streetNumber || null, [
          Validators.maxLength(FieldLimit.STREET_NUMBER),
        ]),
        postalCode: new FormControl(stock?.stockAddress?.postalCode || null, [
          Validators.maxLength(FieldLimit.POSTAL_CODE),
        ]),
        city: new FormControl(stock?.stockAddress?.city || null, [Validators.maxLength(FieldLimit.CITY)]),
        createGeofence: new FormControl(this.shouldCreateGeofenceByDefault()),
        lat: new FormControl(stock?.location?.lat || null),
        lon: new FormControl(stock?.location?.lon || null),
      }),
    });

    this.form.controls.stockType.valueChanges.subscribe((type) => {
      const orgControl = this.form.controls.stockData.controls.organisationId;
      if (type === StockType.MAIN) {
        orgControl.disable();
        orgControl.clearValidators();
      } else {
        orgControl.enable();
        orgControl.setValidators(Validators.required);
      }
      orgControl.updateValueAndValidity();
    });

    return this.form;
  }

  getCreateCommand(): CreateStockCommand {
    const value = this.form.getRawValue();
    const isStockWithLocation = isLocationType(value.stockType!);

    return {
      stockName: value.stockData.stockName!,
      stockType: value.stockType!,
      stockCostCenter: value.stockData.stockCostCenter || '',
      organisationId: value.stockData.organisationId!,
      stockAddress: {
        street: isStockWithLocation ? value.stockAddress.street || '' : '',
        streetNumber: isStockWithLocation ? value.stockAddress.streetNumber || '' : '',
        postalCode: isStockWithLocation ? value.stockAddress.postalCode || '' : '',
        city: isStockWithLocation ? value.stockAddress.city || '' : '',
      },
      createGeofence:
        this.accessService.canManageGeofence() && isStockWithLocation && !!value.stockAddress.createGeofence,
      isDispositionGeofence: this.authService.hasModule(Modules.AUTOMATED_TRANSFERS) && isStockWithLocation,
      location: {
        lat: isStockWithLocation ? value.stockAddress.lat || 0 : 0,
        lon: isStockWithLocation ? value.stockAddress.lon || 0 : 0,
      },
    };
  }

  getUpdateCommand(): UpdateStockCommand {
    const stock = this.$stock();
    if (!stock) throw new Error('Stock is required for update');

    const value = this.form.getRawValue();
    const isStockWithLocation = isLocationType(value.stockType!);
    return {
      stockId: stock.stockId,
      stockName: value.stockData.stockName!,
      stockType: value.stockType!,
      stockCostCenter: value.stockData.stockCostCenter || '',
      organisationId: value.stockData.organisationId!,
      stockAddress: {
        street: isStockWithLocation ? value.stockAddress.street || '' : '',
        streetNumber: isStockWithLocation ? value.stockAddress.streetNumber || '' : '',
        postalCode: isStockWithLocation ? value.stockAddress.postalCode || '' : '',
        city: isStockWithLocation ? value.stockAddress.city || '' : '',
      },
      location: {
        lat: isStockWithLocation ? value.stockAddress.lat || 0 : 0,
        lon: isStockWithLocation ? value.stockAddress.lon || 0 : 0,
      },
    };
  }

  private translate(key: string, interpolateParams?: object): string {
    return this.languageService.getInstant(key, interpolateParams);
  }

  private shouldCreateGeofenceByDefault(): boolean {
    return (
      this.accessService.canManageGeofence() && this.userConfigurationService.getStockAddCreateGeofenceConfiguration()
    );
  }
}
